// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { createReference, formatHumanName } from '@medplum/core';
import type {
  DocumentReference,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { withTenantScope } from './tenant';

export const QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM = 'https://polarissleep.com/document-type';
export const QUESTIONNAIRE_DOCUMENT_TYPE_CODE = 'questionnaire-response';
export const QUESTIONNAIRE_DOCUMENT_SOURCE_EXTENSION =
  'https://polarissleep.com/extensions/source-questionnaire-response';
export const QUESTIONNAIRE_DOCUMENT_NAME_EXTENSION = 'https://polarissleep.com/extensions/source-questionnaire-name';

interface PdfBlock {
  text?: string;
  style?: string;
  margin?: [number, number, number, number];
  stack?: PdfBlock[];
}

function valueToString(answer: NonNullable<QuestionnaireResponseItem['answer']>[number] | undefined): string {
  if (!answer) {
    return '—';
  }
  if (answer.valueBoolean !== undefined) {
    return answer.valueBoolean ? 'Yes' : 'No';
  }
  if (answer.valueInteger !== undefined) {
    return String(answer.valueInteger);
  }
  if (answer.valueDecimal !== undefined) {
    return String(answer.valueDecimal);
  }
  if (answer.valueDate !== undefined) {
    return answer.valueDate;
  }
  if (answer.valueDateTime !== undefined) {
    return answer.valueDateTime;
  }
  if (answer.valueTime !== undefined) {
    return answer.valueTime;
  }
  if (answer.valueCoding) {
    return answer.valueCoding.display ?? answer.valueCoding.code ?? '—';
  }
  if (answer.valueQuantity) {
    return `${answer.valueQuantity.value ?? ''} ${answer.valueQuantity.unit ?? ''}`.trim();
  }
  if (answer.valueReference) {
    return answer.valueReference.display ?? answer.valueReference.reference ?? '—';
  }
  return answer.valueString ?? '—';
}

function findResponseItem(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string
): QuestionnaireResponseItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId) {
      return item;
    }
    const nested = findResponseItem(item.item, linkId);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

function buildBlocks(questionnaire: Questionnaire, response: QuestionnaireResponse): PdfBlock[] {
  const blocks: PdfBlock[] = [];
  const walk = (items: Questionnaire['item']): void => {
    for (const item of items ?? []) {
      const responseItem = item.linkId ? findResponseItem(response.item, item.linkId) : undefined;
      if (item.type === 'group' || item.type === 'display') {
        if (item.text) {
          blocks.push({ text: item.text, style: 'section', margin: [0, 12, 0, 4] });
        }
        walk(item.item);
        continue;
      }
      if (!item.text) {
        continue;
      }
      const answers = responseItem?.answer ?? [];
      blocks.push({
        stack: [
          { text: item.text, style: 'question' },
          { text: answers.length ? answers.map(valueToString).join(', ') : '—', style: 'answer' },
        ],
      });
    }
  };
  walk(questionnaire.item);
  return blocks;
}

async function createPdfBlob(
  questionnaire: Questionnaire,
  response: QuestionnaireResponse,
  patient: Patient
): Promise<Blob> {
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? pdfMake.vfs;
  const definition = {
    content: [
      { text: questionnaire.title ?? questionnaire.name ?? 'Questionnaire', style: 'title' },
      {
        text: `Patient: ${patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient'}`,
        margin: [0, 0, 0, 2],
      },
      { text: `Completed: ${response.authored ?? response.meta?.lastUpdated ?? '—'}`, margin: [0, 0, 0, 12] },
      ...buildBlocks(questionnaire, response),
    ],
    styles: {
      title: { fontSize: 18, bold: true, color: '#640040', margin: [0, 0, 0, 12] },
      section: { fontSize: 12, bold: true, color: '#640040' },
      question: { fontSize: 10, bold: true, margin: [0, 4, 0, 1] },
      answer: { fontSize: 10, margin: [0, 0, 0, 5] },
    },
    defaultStyle: { fontSize: 10 },
  };
  return new Promise((resolve) => {
    (pdfMake.createPdf(definition) as unknown as { getBlob: (callback: (blob: Blob) => void) => void }).getBlob(
      resolve
    );
  });
}

function extension(url: string, value: Record<string, unknown>): { url: string; [key: string]: unknown } {
  return { url, ...value };
}

export function getQuestionnaireDocumentSource(doc: DocumentReference): {
  responseId?: string;
  questionnaireName?: string;
} {
  const source = doc.extension?.find((item) => item.url === QUESTIONNAIRE_DOCUMENT_SOURCE_EXTENSION)?.valueReference
    ?.reference;
  const questionnaireName = doc.extension?.find(
    (item) => item.url === QUESTIONNAIRE_DOCUMENT_NAME_EXTENSION
  )?.valueString;
  return {
    responseId: source?.startsWith('QuestionnaireResponse/') ? source.replace('QuestionnaireResponse/', '') : undefined,
    questionnaireName,
  };
}

export async function createVersionedQuestionnaireDocument(input: {
  medplum: MedplumClient;
  patient: Patient;
  practitioner?: Practitioner;
  questionnaire: Questionnaire;
  response: QuestionnaireResponse;
  previous?: DocumentReference;
}): Promise<DocumentReference> {
  const { medplum, patient, practitioner, questionnaire, response, previous } = input;
  const name = questionnaire.title ?? questionnaire.name ?? 'Questionnaire';
  const now = new Date();
  const date = now.toISOString();
  if (previous?.id) {
    await medplum.updateResource<DocumentReference>({
      ...previous,
      status: 'superseded',
      content: previous.content?.map((content, index) =>
        index === 0
          ? {
              ...content,
              attachment: {
                ...content.attachment,
                title: `${content.attachment?.title ?? name} — Superseded ${now.toLocaleDateString()}`,
              },
            }
          : content
      ),
    });
  }
  const blob = await createPdfBlob(questionnaire, response, patient);
  const binary = await medplum.createBinary(
    blob,
    `${name.replaceAll(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`,
    'application/pdf'
  );
  return medplum.createResource<DocumentReference>(
    withTenantScope({
      resourceType: 'DocumentReference',
      status: 'current',
      subject: createReference(patient),
      date,
      author: practitioner ? [createReference(practitioner)] : undefined,
      type: {
        coding: [{ system: QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM, code: QUESTIONNAIRE_DOCUMENT_TYPE_CODE, display: name }],
      },
      description: `Generated from ${name}. Edit source answers to create a new version.`,
      extension: [
        extension(QUESTIONNAIRE_DOCUMENT_SOURCE_EXTENSION, {
          valueReference: { reference: `QuestionnaireResponse/${response.id}` },
        }),
        extension(QUESTIONNAIRE_DOCUMENT_NAME_EXTENSION, { valueString: questionnaire.name ?? name }),
      ],
      content: [{ attachment: { title: name, url: `Binary/${binary.id}`, contentType: 'application/pdf' } }],
    })
  );
}
